import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  pushSupported, getPermissionStatus, getCurrentSubscription,
  enablePush, disablePush, sendTestPush,
} from '../utils/push';

// Återanvändbar push-inställningskort (admin & worker).
const PushSettingsCard = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!pushSupported()) {
      setSupported(false);
      return;
    }
    setPermission(await getPermissionStatus());
    setSubscribed(!!(await getCurrentSubscription()));
  };

  useEffect(() => { refresh(); }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success('Push-notiser aktiverade!');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Kunde inte aktivera push');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success('Push-notiser inaktiverade');
      await refresh();
    } catch (e) {
      toast.error('Kunde inte stänga av');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const res = await sendTestPush();
      const delivered = res.data?.delivered ?? 0;
      if (delivered > 0) toast.success('Testnotis skickad — kolla notiserna!');
      else toast.error('Ingen aktiv prenumeration hittades');
    } catch (e) {
      toast.error('Testet misslyckades');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="bg-card border border-border rounded p-6 max-w-lg" data-testid="push-not-supported">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={18} className="text-muted-foreground" />
          </div>
          <div>
            <p className="font-bold text-sm">Push stöds inte i denna webbläsare</p>
            <p className="text-xs text-muted-foreground mt-1">
              Använd Chrome, Edge eller Firefox på desktop, eller installera Operio som app (PWA) på din telefon för att få notiser.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="bg-card border border-border rounded p-6 max-w-lg" data-testid="push-denied">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <BellOff size={18} className="text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="font-bold text-sm">Notiser är blockerade</p>
            <p className="text-xs text-muted-foreground mt-1">
              Du har tidigare nekat notiser. Öppna webbläsarens platsinställningar (klicka på låset i adressfältet) och tillåt notiser, ladda sedan om sidan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded p-6 max-w-lg space-y-4" data-testid="push-settings-card">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${subscribed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
          {subscribed ? <BellRing size={18} className="text-green-600 dark:text-green-400" /> : <Bell size={18} className="text-muted-foreground" />}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold">Push-notiser i webbläsaren</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Få notiser direkt i webbläsaren eller på telefonen (om Operio är installerad som app) — t.ex. nya ÄTA, milstolpar och utstämplings­påminnelser.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        {!subscribed ? (
          <Button onClick={handleEnable} disabled={busy} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="push-enable-btn">
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Bell size={14} className="mr-2" />}
            Aktivera notiser
          </Button>
        ) : (
          <>
            <Button onClick={handleTest} variant="outline" disabled={busy} data-testid="push-test-btn">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BellRing size={14} className="mr-2" />}
              Skicka testnotis
            </Button>
            <Button onClick={handleDisable} variant="outline" disabled={busy} data-testid="push-disable-btn">
              <BellOff size={14} className="mr-2" /> Stäng av
            </Button>
          </>
        )}
      </div>
      {subscribed && (
        <p className="text-[11px] text-green-700 dark:text-green-400 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Aktivt på denna enhet
        </p>
      )}
    </div>
  );
};

export default PushSettingsCard;
